var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
/**
 * createSubForm
 * @author georgemo
 */
import React, { useEffect } from 'react';
import equal from 'fast-deep-equal';
export default function createSubForm(store, namespace) {
    var Field = store.Field, useValues = store.useValues, useTouchedState = store.useTouchedState;
    var originStore = 'withForm' in store ? store : store.store;
    var originUesError = originStore.useErrors;
    var namespacePath = "".concat('namespacePath' in store ? "".concat(store.namespacePath, ".") : '').concat(namespace);
    var useSubValues = function () {
        var _a = useValues(), values = _a.values, setValues = _a.setValues, initialValues = _a.initialValues;
        var resetErrors = useSubErrors().resetErrors;
        var setSubValues = function (data, value) {
            var n = namespace;
            var sv = 'store' in store ? setValues : setValues;
            if (typeof data === 'string') {
                sv(function (values) {
                    var _a;
                    values[n] = __assign(__assign({}, values[n]), (_a = {}, _a[data] = value, _a));
                });
            }
            else if (typeof data === 'function') {
                sv(function (values) {
                    data(values[n]);
                });
            }
            else if (typeof data === 'object') {
                sv(function (values) {
                    values[n] = __assign(__assign({}, values[n]), data);
                });
            }
        };
        var resetSubValue = function (vs) {
            var n = namespace;
            var sv = 'store' in store ? setValues : setValues;
            var data = vs || initialValues[namespace];
            sv(function (values) {
                values[n] = __assign({}, data);
            });
            resetErrors();
        };
        var getDirtyState = function (field) {
            var subInitialValues = (initialValues === null || initialValues === void 0 ? void 0 : initialValues[namespace]) || {};
            var subValues = (values === null || values === void 0 ? void 0 : values[namespace]) || {};
            var checkFieldKeys = [];
            var dirtyState = {};
            var checkIfFieldDirty = function (field) { return !equal(subInitialValues === null || subInitialValues === void 0 ? void 0 : subInitialValues[field], subValues === null || subValues === void 0 ? void 0 : subValues[field]); };
            if (typeof field === 'string') {
                checkFieldKeys = [field];
            }
            else if (Array.isArray(field)) {
                checkFieldKeys = field;
            }
            else if (field === undefined) {
                checkFieldKeys = Object.keys(subValues);
            }
            checkFieldKeys.forEach(function (field) {
                dirtyState[field] = checkIfFieldDirty(field);
            });
            return dirtyState;
        };
        return {
            initialValues: initialValues[namespace],
            values: values[namespace] || {},
            setValues: setSubValues,
            resetValues: resetSubValue,
            getDirtyState: getDirtyState,
        };
    };
    var useSubErrors = function () {
        var _a = originUesError(), setError = _a.setError, removeError = _a.removeError, _b = _a.errors, errors = _b === void 0 ? {} : _b;
        var resetSubErrors = function () {
            setError(function (errors) {
                var e_1, _a;
                try {
                    for (var _b = __values(Object.keys(errors)), _c = _b.next(); !_c.done; _c = _b.next()) {
                        var key = _c.value;
                        if (key.startsWith(namespacePath)) {
                            delete errors[key];
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
            });
        };
        var setSubError = function (key, error) {
            setError("".concat(namespacePath, ".").concat(key), error);
        };
        var removeSubError = function (key) {
            removeError("".concat(namespacePath, ".").concat(key));
        };
        var subErrors = Object.keys(errors).reduce(function (result, key) {
            if (key.startsWith(namespacePath)) {
                result[key] = errors[key];
            }
            return result;
        }, {});
        return {
            errors: subErrors,
            setError: setSubError,
            removeError: removeSubError,
            resetErrors: resetSubErrors,
        };
    };
    var useSubTouchedState = function () {
        var _a = useTouchedState(), touchedState = _a.touchedState, setTouchedState = _a.setTouchedState;
        var subTouchedState = Object.keys(touchedState).reduce(function (result, key) {
            if (key.startsWith(namespacePath)) {
                result[key] = touchedState[key];
            }
            return result;
        }, {});
        return {
            touchedState: subTouchedState,
            setTouchedState: setTouchedState,
        };
    };
    function SubField(props) {
        var _a = useSubValues(), values = _a.values, setValues = _a.setValues;
        var _b = useSubTouchedState(), touchedState = _b.touchedState, setTouchedState = _b.setTouchedState;
        var f = props.field;
        useEffect(function () {
            var _a;
            // 由于 SubField 会多次向上渲染触发 useEffect，
            // 所以这里只对最下层的 field key 做 touchedState 初始化
            if (f && !((_a = f) === null || _a === void 0 ? void 0 : _a.includes('.')) && touchedState["".concat(namespacePath, ".").concat(f)] === undefined) {
                setTouchedState("".concat(namespacePath, ".").concat(f), false);
            }
        }, []);
        return (React.createElement(Field, __assign({ onChange: function (value) {
                // 由于 SubField 向上渲染时，最下层的 onChange 总是会覆盖上层的 onChange，
                // 所以这里拿到的 f 总是最下层的 field key
                if (f) {
                    setTouchedState("".concat(namespacePath, ".").concat(f), true);
                }
                setValues(f, value);
            }, value: (values && values[f]) }, props, { field: "".concat(namespace, ".").concat(props.field) })));
    }
    return {
        Field: SubField,
        useValues: useSubValues,
        useErrors: useSubErrors,
        useTouchedState: useSubTouchedState,
        namespacePath: namespacePath,
        store: originStore,
    };
}
